import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import DashboardPagination from '../components/DashboardPagination';
import OfficialReportLayout, {
  OfficialField,
  OfficialSectionTitle,
  OfficialTableCell,
} from '../components/print/OfficialReportLayout';
import funcaoOrcamentariaService from '../services/funcaoOrcamentariaService';
import reportService, { RelatorioInstituicaoItem } from '../services/reportService';
import { STATUS_OSC_LABELS, StatusOSC } from '../types/statusOSC.types';
import { UI_THEME } from '../ui/theme';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const MODALIDADE_OPTIONS = [
  { value: 'TERMO_COLABORACAO', label: 'Termo de colaboração' },
  { value: 'TERMO_FOMENTO', label: 'Termo de fomento' },
];
const sortOptions = [
  { value: 'razaoSocial,asc', label: 'Razão social A-Z' },
  { value: 'nomeFantasia,asc', label: 'Nome fantasia A-Z' },
  { value: 'statusOSC,asc', label: 'Etapa OSC A-Z' },
  { value: 'modalidade,asc', label: 'Modalidade A-Z' },
  { value: 'dataCriacao,desc', label: 'Cadastro mais recente' },
  { value: 'dataCriacao,asc', label: 'Cadastro mais antigo' },
];

const formatDateTime = (value?: string) => {
  if (!value) return 'Não informado';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const formatCnpj = (value?: string) => {
  if (!value) return 'Não informado';
  return value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

const modalidadeLabel = (value?: string) => MODALIDADE_OPTIONS.find((item) => item.value === value)?.label || value || 'Todas';
const statusOscLabel = (value?: string) => (value && value in STATUS_OSC_LABELS ? STATUS_OSC_LABELS[value as StatusOSC] : value || 'Todos');

const ReportsInstituicoesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<RelatorioInstituicaoItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);

  const isSaas = UI_THEME === 'saas';
  const primaryButtonClass = isSaas
    ? 'inline-flex items-center justify-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'inline-flex items-center justify-center rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-blue-600 dark:hover:bg-blue-500';
  const secondaryButtonClass = 'inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';
  const darkButtonClass = 'inline-flex items-center justify-center rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-600 dark:hover:bg-slate-500';
  const inputClass = 'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40 print:border-slate-300 print:bg-white print:text-slate-700';
  const labelClass = 'space-y-1 text-sm text-slate-600 dark:text-slate-300 print:text-slate-600';

  const page = Math.max(Number(searchParams.get('page') || '1'), 1);
  const size = PAGE_SIZE_OPTIONS.includes(Number(searchParams.get('size'))) ? Number(searchParams.get('size')) : 20;
  const sort = searchParams.get('sort') || 'razaoSocial,asc';

  const filters = useMemo(() => ({
    statusOSC: searchParams.get('statusOSC') || '',
    modalidade: searchParams.get('modalidade') || '',
    funcaoCodigo: searchParams.get('funcaoCodigo') || '',
    dataInicio: searchParams.get('dataInicio') || '',
    dataFim: searchParams.get('dataFim') || '',
  }), [searchParams]);
  const funcoesQuery = useQuery({
    queryKey: ['funcoes-orcamentarias'],
    queryFn: () => funcaoOrcamentariaService.list(),
    staleTime: 5 * 60 * 1000,
  });
  const funcoes = funcoesQuery.data ?? [];
  const areaLabel = (codigo?: string) => (codigo ? funcoes.find((f) => f.codigo === codigo)?.descricao || codigo : 'Todas');
  const [draftFilters, setDraftFilters] = useState(filters);
  const [draftSort, setDraftSort] = useState(sort);

  useEffect(() => {
    setDraftFilters(filters);
    setDraftSort(sort);
  }, [filters, sort]);

  const updateParams = (updates: Record<string, string>, resetPage = true) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    if (resetPage) {
      params.set('page', '1');
    }
    setSearchParams(params);
  };

  const handleVisualizar = () => {
    const params = new URLSearchParams(searchParams);
    Object.entries(draftFilters).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    params.set('sort', draftSort);
    params.set('page', '1');
    params.set('size', String(size));
    setSearchParams(params);
  };

  const clearFilters = () => {
    setDraftFilters({
      statusOSC: '',
      modalidade: '',
      funcaoCodigo: '',
      dataInicio: '',
      dataFim: '',
    });
    setDraftSort('razaoSocial,asc');
    setSearchParams(new URLSearchParams({ page: '1', size: String(size), sort: 'razaoSocial,asc' }));
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await reportService.getInstituicoes({
          ...filters,
          page: page - 1,
          size,
          sort,
        });
        if (!active) return;
        setItems(Array.isArray(response.items) ? response.items : []);
        setTotalItems(response.totalItems || 0);
      } catch (err: any) {
        if (!active) return;
        setItems([]);
        setTotalItems(0);
        setError(err?.response?.data?.error || 'Não foi possível carregar o relatório de instituições.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [filters, page, size, sort]);

  const handleExport = async (type: 'xlsx' | 'pdf') => {
    try {
      setExporting(type);
      if (type === 'xlsx') {
        await reportService.exportInstituicoesXlsx({ ...filters, sort });
      } else {
        await reportService.exportInstituicoesPdf({ ...filters, sort });
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || `Não foi possível exportar o arquivo ${type.toUpperCase()}.`);
    } finally {
      setExporting(null);
    }
  };

  const summaryFields = [
    <OfficialField key="total" label="Registros filtrados" value={String(totalItems)} />,
    <OfficialField key="pagina" label="Registros na página" value={String(items.length)} />,
    <OfficialField key="modalidade" label="Com modalidade" value={String(items.filter((item) => item.modalidadePlano).length)} />,
    <OfficialField key="aprovadas" label="Instituições aprovadas" value={String(items.filter((item) => item.statusOSC === StatusOSC.APROVADO).length)} />,
  ];

  return (
    <OfficialReportLayout
      moduleLabel="Cadastro de instituição"
      title="Relatório de instituições"
      subtitle={isSaas
        ? 'Consulta consolidada das organizações cadastradas, com etapa, modalidade vinculada, áreas de atuação e período de criação.'
        : 'Consulta consolidada das OSCs cadastradas, com etapa, modalidade vinculada, áreas de atuação e período de criação dentro do padrão oficial de impressão.'}
      summaryFields={summaryFields}
      toolbar={
        <>
          <button type="button" onClick={handleVisualizar} className={primaryButtonClass}>
            Visualizar
          </button>
          <button
            type="button"
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null}
            className={secondaryButtonClass}
          >
            {exporting === 'xlsx' ? 'Exportando XLSX...' : 'Exportar XLSX'}
          </button>
          <button
            type="button"
            onClick={() => handleExport('pdf')}
            disabled={exporting !== null}
            className={darkButtonClass}
          >
            {exporting === 'pdf' ? 'Exportando PDF...' : 'Exportar PDF'}
          </button>
          <button type="button" onClick={clearFilters} className={secondaryButtonClass}>
            Limpar filtros
          </button>
        </>
      }
    >
      <OfficialSectionTitle number="1" title="Filtros de consulta" aside="A visualização abaixo reflete somente os parâmetros aplicados." />
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
        <label className={labelClass}>
          <span className="font-medium">Etapa OSC</span>
          <select
            value={draftFilters.statusOSC}
            onChange={(e) => setDraftFilters((current) => ({ ...current, statusOSC: e.target.value }))}
            className={inputClass}
          >
            <option value="">Todos</option>
            {Object.entries(STATUS_OSC_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          <span className="font-medium">Modalidade</span>
          <select
            value={draftFilters.modalidade}
            onChange={(e) => setDraftFilters((current) => ({ ...current, modalidade: e.target.value }))}
            className={inputClass}
          >
            <option value="">Todas</option>
            {MODALIDADE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          <span className="font-medium">Área de atuação</span>
          <select
            value={draftFilters.funcaoCodigo}
            onChange={(e) => setDraftFilters((current) => ({ ...current, funcaoCodigo: e.target.value }))}
            className={inputClass}
          >
            <option value="">Todas</option>
            {funcoes.map((funcao) => (
              <option key={funcao.codigo} value={funcao.codigo}>{funcao.descricao}</option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          <span className="font-medium">Data inicial</span>
          <input
            type="date"
            value={draftFilters.dataInicio}
            onChange={(e) => setDraftFilters((current) => ({ ...current, dataInicio: e.target.value }))}
            className={inputClass}
          />
        </label>
        <label className={labelClass}>
          <span className="font-medium">Data final</span>
          <input
            type="date"
            value={draftFilters.dataFim}
            onChange={(e) => setDraftFilters((current) => ({ ...current, dataFim: e.target.value }))}
            className={inputClass}
          />
        </label>
        <label className={labelClass}>
          <span className="font-medium">Ordenação</span>
          <select
            value={draftSort}
            onChange={(e) => setDraftSort(e.target.value)}
            className={inputClass}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>

      <OfficialSectionTitle number="2" title="Parâmetros aplicados" />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-5">
        <OfficialField label="Etapa OSC" value={statusOscLabel(filters.statusOSC)} />
        <OfficialField label="Modalidade" value={modalidadeLabel(filters.modalidade)} />
        <OfficialField label="Área de atuação" value={areaLabel(filters.funcaoCodigo)} />
        <OfficialField label="Data inicial" value={filters.dataInicio ? formatDateTime(`${filters.dataInicio}T00:00:00`) : 'Todos'} />
        <OfficialField label="Data final" value={filters.dataFim ? formatDateTime(`${filters.dataFim}T23:59:59`) : 'Todos'} />
      </div>

      <OfficialSectionTitle number="3" title="Relação de instituições" />
      {loading ? (
        <div className="rounded-xl border border-slate-200 px-4 py-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
          Carregando relatório de instituições...
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 px-4 py-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
          Nenhuma instituição encontrada para os filtros informados.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <OfficialTableCell head>Instituição</OfficialTableCell>
                  <OfficialTableCell head>CNPJ</OfficialTableCell>
                  <OfficialTableCell head>Etapa OSC</OfficialTableCell>
                  <OfficialTableCell head>Modalidade</OfficialTableCell>
                  <OfficialTableCell head>Áreas de atuação</OfficialTableCell>
                  <OfficialTableCell head>Cidade / UF</OfficialTableCell>
                  <OfficialTableCell head align="center">Cadastro</OfficialTableCell>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.institutionId}>
                    <OfficialTableCell>
                      <div className="font-semibold">{item.razaoSocial || 'Não informado'}</div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400">{item.nomeFantasia || item.institutionId}</div>
                    </OfficialTableCell>
                    <OfficialTableCell>{formatCnpj(item.cnpj)}</OfficialTableCell>
                    <OfficialTableCell>{statusOscLabel(item.statusOSC)}</OfficialTableCell>
                    <OfficialTableCell>{modalidadeLabel(item.modalidadePlano)}</OfficialTableCell>
                    <OfficialTableCell>{item.areasAtuacao?.length ? item.areasAtuacao.join(', ') : 'Não informado'}</OfficialTableCell>
                    <OfficialTableCell>{[item.cidade, item.uf].filter(Boolean).join(' / ') || 'Não informado'}</OfficialTableCell>
                    <OfficialTableCell align="center">{formatDateTime(item.dataCriacao)}</OfficialTableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DashboardPagination
            page={page}
            pageSize={size}
            totalItems={totalItems}
            onPageChange={(value) => updateParams({ page: String(value) }, false)}
            onPageSizeChange={(value) => {
              const params = new URLSearchParams(searchParams);
              params.set('size', String(value));
              params.set('page', '1');
              setSearchParams(params);
            }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            itemLabel="instituições"
          />
        </div>
      )}
    </OfficialReportLayout>
  );
};

export default ReportsInstituicoesPage;
